import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, SESSION_MODES, applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getNotifCount } from '../../services/api';
import QuotaBanner from './QuotaBanner';
import TrialBanner from './TrialBanner';
import DiscountBanner from './DiscountBanner';
import PaymentGate from './PaymentGate';

export default function TenantLayout() {
  const { user, logout, hasPermission, hasFeature, reloadUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [unread,     setUnread]     = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const tenant  = user?.tenant;
  const initial = user?.name?.[0]?.toUpperCase() || '?';

  // Apply tenant branding (cor primária + variação dark gerada automaticamente)
  useEffect(() => {
    if (tenant?.primary_color) {
      applyTenantBranding(tenant.primary_color);
    }
    return () => clearTenantBranding();
  }, [tenant]);

  // Poll unread notification count every 30s
  useEffect(() => {
    if (!hasFeature('notifications')) return;
    const poll = () => getNotifCount().then(r => setUnread(r.data.unread_count || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, []);

  // Redirect to onboarding if needed
  useEffect(() => {
    if (user?.onboarding_needed) {
      navigate('/app/onboarding', { replace: true });
    }
  }, [user?.onboarding_needed]);

  const handleExitImpersonate = async () => {
    try {
      const { stopImpersonating } = await import('../../services/api');
      await stopImpersonating();
      localStorage.removeItem('master_token');
      // Recarregar contexto antes de redirecionar — garante que o AuthContext
      // volte a MASTER_GLOBAL sem depender de page reload completo.
      await reloadUser();
    } catch {}
    window.location.href = '/master';
  };

  const canViewAppts = hasPermission('VIEW_ALL_APPOINTMENTS') || hasPermission('VIEW_OWN_APPOINTMENTS');

  const allNavItems = [
    // ── Dia a dia ─────────────────────────────────────────────
    { to: '/app',               label: 'Início',           icon: '📊', show: true, end: true, group: 'Dia a dia' },
    { to: '/app/appointments',  label: 'Agenda',           icon: '📅', show: hasFeature('appointments') && canViewAppts, group: 'Dia a dia' },
    { to: '/app/clients',       label: 'Clientes',         icon: '👤', show: hasFeature('crm'), group: 'Dia a dia' },
    { to: '/app/notifications', label: 'Alertas',          icon: '🔔', show: hasFeature('notifications') && hasPermission('VIEW_NOTIFICATIONS'), badge: unread, group: 'Dia a dia' },

    // ── Meu negócio ───────────────────────────────────────────
    { to: '/app/services',      label: 'Serviços',         icon: '🛎️', show: hasPermission('MANAGE_TENANT_SETTINGS'), group: 'Meu negócio' },
    { to: '/app/business-hours',label: 'Horários',         icon: '🕐', show: hasPermission('MANAGE_TENANT_SETTINGS'), group: 'Meu negócio' },
    { to: '/app/users',         label: 'Equipe',           icon: '👥', show: hasPermission('MANAGE_USERS'), group: 'Meu negócio' },
    { to: '/app/units',         label: 'Unidades',         icon: '🏢', show: hasFeature('multi_unit') && hasPermission('MANAGE_TENANT_SETTINGS'), group: 'Meu negócio' },

    // ── Crescimento ───────────────────────────────────────────
    { to: '/app/waitlist',      label: 'Lista de Espera',  icon: '⏳', show: hasFeature('waitlist'), group: 'Crescimento' },
    { to: '/app/nps',           label: 'Satisfação',       icon: '⭐', show: hasFeature('nps'), group: 'Crescimento' },
    { to: '/app/loyalty',       label: 'Fidelidade',       icon: '🎯', show: hasFeature('loyalty'), group: 'Crescimento' },
    { to: '/app/packages',      label: 'Pacotes',          icon: '📦', show: hasFeature('packages'), group: 'Crescimento' },
    { to: '/app/intake-forms',  label: 'Formulários',      icon: '📝', show: hasFeature('intake_forms') && hasPermission('MANAGE_TENANT_SETTINGS'), group: 'Crescimento' },

    // ── Financeiro ────────────────────────────────────────────
    { to: '/app/billing',       label: 'Financeiro',       icon: '💰', show: hasFeature('billing') && hasPermission('VIEW_BILLING'), group: 'Financeiro' },
    { to: '/app/deposits',      label: 'Recebimentos',     icon: '💳', show: hasFeature('deposits'), group: 'Financeiro' },

    // ── Configurações ─────────────────────────────────────────
    { to: '/app/integrations',  label: 'Integrações',      icon: '🔗', show: hasFeature('google_calendar') || hasFeature('whatsapp'), group: 'Configurações' },
    { to: '/app/api-tokens',    label: 'API',              icon: '🔑', show: hasFeature('api_access') && hasPermission('MANAGE_TENANT_SETTINGS'), group: 'Configurações' },
    { to: '/app/webhooks',      label: 'Webhooks',         icon: '🔔', show: hasFeature('webhooks') && hasPermission('MANAGE_TENANT_SETTINGS'), group: 'Configurações' },
    { to: '/app/settings',      label: 'Configurações',    icon: '⚙️', show: hasPermission('MANAGE_TENANT_SETTINGS'), group: 'Configurações' },
    { to: '/app/audit',         label: 'Auditoria',        icon: '🔍', show: user?.mode === SESSION_MODES.MASTER_IMPERSONATED && hasPermission('VIEW_AUDIT_LOGS'), group: 'Configurações' },
  ].filter(i => i.show);

  // Bottom nav: max 5 most important items
  const bottomNavItems = [
    { to: '/app',              label: 'Início',   icon: '📊', show: true, end: true },
    { to: '/app/appointments', label: 'Agenda',   icon: '📅', show: hasFeature('appointments') && canViewAppts },
    { to: '/app/notifications', label: 'Alertas', icon: '🔔', show: hasFeature('notifications'), badge: unread },
    { to: '/app/users',        label: 'Equipe',   icon: '👥', show: hasPermission('MANAGE_USERS') },
    { to: '/app/settings',     label: 'Config',   icon: '⚙️', show: hasPermission('MANAGE_TENANT_SETTINGS') },
  ].filter(i => i.show).slice(0, 5);

  const SidebarContent = () => (
    <>
      <div className="sidebar-brand">
        <div className="logo-row">
          {tenant?.logo_url
            ? <img src={tenant.logo_url} alt="logo" className="logo-img" />
            : <div className="logo-placeholder">{tenant?.name?.[0] || 'A'}</div>
          }
          <h2>{tenant?.name || 'AtendoApp'}</h2>
        </div>
      </div>

      {user?.mode === SESSION_MODES.MASTER_IMPERSONATED && (
        <div className="impersonate-banner">
          <small>Modo Impersonação</small>
          <strong>Operando como {tenant?.name || 'tenant'}</strong>
          <button className="btn-exit-impersonate" onClick={handleExitImpersonate}>← Voltar ao Master</button>
        </div>
      )}

      <nav className="sidebar-nav">
        {(() => {
          let lastGroup = null;
          return allNavItems.map(item => {
            const groupChanged = item.group && item.group !== lastGroup;
            lastGroup = item.group;
            return (
              <React.Fragment key={item.to}>
                {groupChanged && <span className="sidebar-section-label">{item.group}</span>}
                <NavLink to={item.to} end={item.end}
                  className={({ isActive }) => `sidebar-nav-item${isActive ? ' active' : ''}`}
                  onClick={() => setMobileOpen(false)}>
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                  {item.badge > 0 && <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
                </NavLink>
              </React.Fragment>
            );
          });
        })()}
      </nav>

      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar" style={{ background: 'var(--primary)' }}>{initial}</div>
          <div className="user-info-text">
            <small>{user?.mode === SESSION_MODES.MASTER_IMPERSONATED ? 'Master (Impersonando)' : 'Usuário'}</small>
            <strong>{user?.name}</strong>
          </div>
        </div>
        <button className="btn-theme" onClick={toggleTheme}>
          {theme === 'dark' ? '☀️ Modo claro' : '🌙 Modo escuro'}
        </button>
        <button className="btn-logout" onClick={logout}>Sair</button>
      </div>
    </>
  );

  return (
    <div className="app-layout">
      {/* Overlay for mobile sidebar */}
      <div className={`mobile-overlay${mobileOpen ? ' active' : ''}`} onClick={() => setMobileOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
        <SidebarContent />
      </aside>

      <div className="main-content">
        {/* Mobile top bar */}
        <div className="mobile-header">
          <button className="hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
            ☰
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {tenant?.logo_url
              ? <img src={tenant.logo_url} alt="" style={{ height: 28, objectFit: 'contain', borderRadius: 4 }} />
              : <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--gray-900)' }}>{tenant?.name || 'AtendoApp'}</span>
            }
          </div>
          <div style={{ width: 44 }} /> {/* spacer */}
        </div>

        <TrialBanner />
        <QuotaBanner />
        <DiscountBanner />

        <PaymentGate>
          <Outlet context={{ unread, setUnread }} />
        </PaymentGate>

        {/* Bottom Navigation (mobile only) */}
        <nav className="bottom-nav" aria-label="Navegação principal">
          {bottomNavItems.map(item => (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="bnav-icon">{item.icon}</span>
              <span>{item.label}</span>
              {item.badge > 0 && (
                <span className="bnav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Botão de suporte fixo — abre WhatsApp do AtendoApp */}
        <a
          href="https://wa.me/5531982288243?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20o%20AtendoApp"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            background: '#25d366',
            color: '#fff',
            borderRadius: 50,
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,.2)',
            zIndex: 100,
            textDecoration: 'none',
            fontSize: 24,
          }}
          title="Precisa de ajuda? Fale conosco no WhatsApp"
          aria-label="Suporte via WhatsApp"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.61.609l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.315 0-4.458-.764-6.182-2.054l-.432-.334-2.89.969.969-2.89-.334-.432A9.955 9.955 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
        </a>
      </div>
    </div>
  );
}
